<aside
  class="fixed top-0 h-screen w-60 bg-sidebar border-r border-sidebar-border flex flex-col z-50 transition-all duration-300 left-0"
  [class.translate-x-0]="mobileOpen" [class.-translate-x-full]="!mobileOpen" [class.lg:translate-x-0]="true"
  [class.top-14]="mobileOpen" [class.lg:top-0]="true"
  [class.lg:w-16]="collapsed()">

  <!-- Rail toggle. It rides the sidebar's own edge rather than sitting inside the logo row, so the
       control stays in the same place — and stays findable — whether the sidebar is 240px or 64px.
       Desktop only: a drawer has nothing to collapse to. -->
  <button type="button" (click)="toggleCollapsed()"
    [attr.aria-label]="collapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
    [attr.aria-expanded]="!collapsed()"
    [title]="collapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
    class="hidden lg:flex absolute -right-3 top-5 z-[70] h-6 w-6 items-center justify-center rounded-full
           border border-sidebar-border bg-sidebar text-muted-foreground shadow-sm transition-colors
           hover:bg-sidebar-accent hover:text-foreground">
    <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
      stroke-linecap="round" stroke-linejoin="round">
      <!-- Points where the click will take it: in to collapse, out to expand. -->
      @if (collapsed()) {
      <polyline points="9 18 15 12 9 6" />
      } @else {
      <polyline points="15 18 9 12 15 6" />
      }
    </svg>
  </button>

  <!-- Logo (hidden on mobile since it's in the header). Collapsed, only the mark has room. -->
  <div class="hidden lg:flex h-16 items-center border-b border-sidebar-border"
    [class.px-4]="!collapsed()" [class.justify-center]="collapsed()">
    <!-- The wordmark is 4:1, so the rail gets the square mark rather than a crop of the wide one. -->
    @if (collapsed()) {
      <img src="hashbooks-favicon.png" alt="HashBooks" class="h-8 w-8" />
    } @else {
      <img src="hashbooks-logo.png" alt="HashBooks" class="h-11" />
    }
  </div>

  <!-- Navigation -->
  <nav class="flex-1 py-4 px-3 space-y-4 overflow-y-auto no-scrollbar">
    @for (group of visibleGroups(); track group.label) {
    <div>
      @if (collapsed()) {
      <!-- A rail cannot show "TAX & COMPLIANCE" in 64px, so the grouping survives as a rule between
           icon clusters — the same thing the words were doing, minus the words. -->
      @if (!$first) {
      <div class="mx-3 mb-3 border-t border-sidebar-border"></div>
      }
      } @else if (group.collapsible) {
      <!-- Collapsible "More" group: a toggle that expands the coming-soon items on demand. -->
      <button (click)="toggleMore()"
        class="w-full flex items-center justify-between px-3 mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors">
        <span>{{ group.label }}</span>
        <svg class="w-3.5 h-3.5 transition-transform duration-200" [class.rotate-180]="moreExpanded()"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      } @else {
      <p class="px-3 mb-2 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
        {{ group.label }}
      </p>
      }
      @if (collapsed() ? !group.collapsible : (!group.collapsible || moreExpanded())) {
      <div class="space-y-0.5">
        @for (item of group.items; track item.route) {
        <div class="relative group">
        <a [routerLink]="item.comingSoon || item.modal ? null : item.route" routerLinkActive="bg-sidebar-accent text-primary"
          [routerLinkActiveOptions]="{ paths: item.exact ? 'exact' : 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
          #rla="routerLinkActive" [class.text-muted-foreground]="!rla.isActive" [class.opacity-50]="item.comingSoon"
          [class.cursor-pointer]="item.comingSoon || item.modal"
          (click)="onItemActivate(item, $event)"
          [attr.title]="collapsed() ? item.label : null"
          [class.justify-center]="collapsed()" [class.gap-3]="!collapsed()" [class.px-3]="!collapsed()"
          class="relative flex items-center py-2.5 rounded-lg text-sm font-medium transition-all duration-150 hover:bg-sidebar-accent hover:text-primary">
          <!-- The tint alone is easy to miss against the sidebar; the bar is what makes "you are
               here" readable in a glance, and it survives the rail where the label does not. -->
          @if (rla.isActive) {
          <span class="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary"></span>
          }
          @switch (item.icon) {
          @case ('dashboard') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="9" />
            <rect x="14" y="3" width="7" height="5" />
            <rect x="14" y="12" width="7" height="9" />
            <rect x="3" y="16" width="7" height="5" />
          </svg>
          }
          @case ('portfolio') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
            <path d="M22 12A10 10 0 0 0 12 2v10z" />
          </svg>
          }
          @case ('ai-optimizer') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path
              d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z" />
            <path
              d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z" />
          </svg>
          }
          @case ('harvestable-losses') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
            <polyline points="17 18 23 18 23 12" />
          </svg>
          }
          @case ('transactions') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3.01" y2="6" />
            <line x1="3" y1="12" x2="3.01" y2="12" />
            <line x1="3" y1="18" x2="3.01" y2="18" />
          </svg>
          }
          @case ('spam-shield') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          }
          @case ('tax-center') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
            <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
            <path d="M12 17.5v-11" />
          </svg>
          }
          @case ('tax-accountant') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          }
          @case ('zk-proofs') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          }
          @case ('rwa-assets') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
            <path d="M6 12H4a2 2 0 0 0-2 2v8h4" />
            <path d="M18 9h2a2 2 0 0 1 2 2v11h-4" />
            <path d="M10 6h4" />
            <path d="M10 10h4" />
            <path d="M10 14h4" />
            <path d="M10 18h4" />
          </svg>
          }
          @case ('integrations') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22v-5" />
            <path d="M9 8V2" />
            <path d="M15 8V2" />
            <path d="M18 8v4a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V8Z" />
          </svg>
          }
          @case ('residency') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          }
          @case ('referral') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 12 20 22 4 22 4 12" />
            <rect x="2" y="7" width="20" height="5" />
            <line x1="12" y1="22" x2="12" y2="7" />
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
          </svg>
          }
          @case ('settings') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          }
          @case ('subscription') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <line x1="2" y1="10" x2="22" y2="10" />
          </svg>
          }
          @case ('support') {
          <svg class="w-[18px] h-[18px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          }
          }
          @if (!collapsed()) {
          <span>{{ item.label }}</span>
          }
        </a>
        @if (item.comingSoon) {
        <span
          class="pointer-events-none absolute z-[9999] top-full left-3 mt-1.5 w-max max-w-[190px] p-2.5 rounded-lg shadow-xl border border-border bg-popover text-popover-foreground text-xs leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
          <strong class="flex items-center gap-1.5 text-foreground">
            <svg class="w-3.5 h-3.5 shrink-0 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Coming Soon
          </strong>
          <span class="block text-muted-foreground mt-1">This feature is launching soon — stay tuned.</span>
          <span class="absolute bottom-full left-4 border-4 border-transparent border-b-border"></span>
        </span>
        }
        </div>
        }
      </div>
      }
    </div>
    }
  </nav>

  <!-- Upgrade nudge — free plan only, disappears once they subscribe. Hidden in the rail: a card of
       prose cannot shrink to 64px, and Subscription is a nav item now, so the route stays reachable. -->
  @if (showUpgradeCard() && !collapsed()) {
    <div class="mx-3 mb-2 rounded-xl border border-primary/20 bg-primary/5 p-3">
      <p class="text-xs leading-relaxed text-muted-foreground">
        @if (upgradeDaysLeft() !== null) {
          <span class="font-bold text-foreground">{{ upgradeDaysLeft() }} days</span> left on your
          welcome offer — upgrade to unlock your tax forms.
        } @else {
          Upgrade to unlock your tax forms and unlimited wallets.
        }
      </p>
      <button
        type="button"
        (click)="goToPricing()"
        class="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90"
      >
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3l1.9 5.8H20l-4.9 3.6 1.9 5.8-5-3.7-5 3.7 1.9-5.8L4 8.8h6.1L12 3z" />
        </svg>
        Upgrade plan
      </button>
    </div>
  }

  <!-- User profile pinned to the sidebar bottom. Opens the account menu upward (top-right account
       menu is unchanged). -->
  <!-- #profileArea marks the click-away boundary: a document listener closes the menu on any click
       landing outside this block. A `fixed inset-0` backdrop can't work here — the <aside> always
       carries a translate-x class, and a transformed ancestor makes fixed children position against
       it, so the overlay would only ever cover the sidebar's own column. -->
  <div #profileArea class="relative border-t border-sidebar-border p-3">
    @if (profileMenuOpen()) {
      <!-- Flyout to the right of the sidebar on lg+. Below that the viewport is too narrow for the
           sidebar plus the menu, so it falls back to opening upward in place. Settings, Subscription
           and Tax Accountant all live in the nav now — signing out is the only thing left that is
           not a destination. -->
      <div class="absolute bottom-full left-3 right-3 z-[60] mb-2 rounded-lg border border-border bg-popover py-1 shadow-lg animate-in
                  lg:bottom-3 lg:left-full lg:right-auto lg:mb-0 lg:ml-2 lg:w-44"
        role="menu" aria-label="Account menu">
        <button type="button" role="menuitem" (click)="onLogout()"
          class="flex w-full items-center px-3 py-2 text-sm text-destructive transition-colors hover:bg-muted">
          <svg class="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Log out
        </button>
      </div>
    }

    <button type="button" (click)="toggleProfileMenu()" aria-haspopup="menu"
      [attr.aria-expanded]="profileMenuOpen()"
      [attr.title]="collapsed() ? userName() : null"
      class="relative z-[60] w-full flex items-center rounded-lg py-2 text-left transition-colors hover:bg-sidebar-accent"
      [class.gap-3]="!collapsed()" [class.px-2]="!collapsed()" [class.justify-center]="collapsed()"
      [class.bg-sidebar-accent]="profileMenuOpen()">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {{ userInitials() }}
      </span>
      @if (!collapsed()) {
      <span class="min-w-0 flex-1">
        <span class="block truncate text-sm font-semibold text-foreground">{{ userName() }}</span>
        <span class="block truncate text-xs text-muted-foreground">{{ userEmail() }}</span>
      </span>
      }
      <!-- Points up while the mobile menu opens upward; stays a plain "opens to the side" chevron
           on lg+, where the menu flies out to the right. -->
      @if (!collapsed()) {
      <svg class="w-4 h-4 shrink-0 text-muted-foreground transition-transform lg:rotate-0" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        [class.-rotate-90]="profileMenuOpen()">
        <polyline points="9 18 15 12 9 6" />
      </svg>
      }
    </button>
  </div>

</aside>